import { useMemo, useState } from "react"
import { createPayment } from "../../services/paymentService"
import { fr } from "../../locales/fr"
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import { Label } from "../../components/ui/Label"
import { Select } from "../../components/ui/Select"

const textareaClass = `
  flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground
  placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
  focus-visible:ring-offset-2 focus-visible:ring-offset-background
`

export default function StepPayment({ member, subscription, next, setPayment }) {
  const total = useMemo(() => {
    const raw = subscription?.priceTotal ?? subscription?.price_total ?? subscription?.price
    const n = Number(raw)
    return Number.isFinite(n) ? n : 0
  }, [subscription])

  const [form, setForm] = useState({
    amount: total > 0 ? String(total) : "",
    method: "cash",
    payment_type: "full",
    invoice_notes: "",
  })

  const w = fr.wizard.stepPayment

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const save = async () => {
    const amountNum = parseFloat(String(form.amount).replace(",", "."), 10)
    const payload = {
      member_id: member.id,
      subscription_id: subscription.id,
      amount: amountNum,
      method: form.method,
      payment_type: form.payment_type,
      invoice_notes: form.invoice_notes.trim() || undefined,
    }
    const created = await createPayment(payload)
    setPayment?.(created)
    next(4)
  }

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>{w.title}</CardTitle>
        <CardDescription>
          {total > 0 ? (
            <>
              {w.subscriptionTotal} : <strong>{total.toFixed(2)} MAD</strong>
            </>
          ) : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-lg border border-border bg-muted/40 p-4">
          <p className="text-sm text-muted-foreground">{w.memberLabel}</p>
          <p className="font-semibold text-foreground">
            {member.firstName} {member.lastName}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="wiz-pay-amount">{w.amount}</Label>
          <Input
            id="wiz-pay-amount"
            name="amount"
            type="number"
            min="0"
            step="0.01"
            value={form.amount}
            onChange={handleChange}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="wiz-payment-type">{w.paymentTypeLabel}</Label>
          <Select
            id="wiz-payment-type"
            name="payment_type"
            value={form.payment_type}
            onChange={handleChange}
          >
            <option value="full">{w.paymentTypeFull}</option>
            <option value="partial">{w.paymentTypePartial}</option>
          </Select>
          <p className="text-xs text-muted-foreground">{w.paymentTypeHint}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="wiz-pay-method">{w.method}</Label>
          <Select id="wiz-pay-method" name="method" value={form.method} onChange={handleChange}>
            <option value="cash">{fr.paymentMethod.cash}</option>
            <option value="card">{fr.paymentMethod.card}</option>
            <option value="transfer">{fr.paymentMethod.transfer}</option>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="wiz-invoice-notes">{w.invoiceNotes}</Label>
          <textarea
            id="wiz-invoice-notes"
            name="invoice_notes"
            value={form.invoice_notes}
            onChange={handleChange}
            rows={3}
            placeholder={w.invoiceNotesPlaceholder}
            className={textareaClass}
          />
        </div>
      </CardContent>
      <CardFooter className="flex justify-end border-t border-border pt-6">
        <Button type="button" onClick={save}>
          {w.confirm}
        </Button>
      </CardFooter>
    </Card>
  )
}
